import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Container,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Login,
  Email,
  ArrowBack,
  WhatsApp,
  CheckCircle,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Helmet } from 'react-helmet-async';

import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { LoginCredentials } from '../../types';
import { authService } from '../../services/authService';
import { ThemeMenuButton } from '../../layout/ThemeMenuButton';
import { isCopaTheme } from '../../themes/themeRegistry';
import logoWhite from '../../assets/logo-white.png';
import {
  getLoginCardHeaderBackground,
  getLoginCardSx,
  getLoginFooterTextColor,
  getLoginPageBackground,
} from './loginStyles';

// Constantes para rate limiting
const MAX_ATTEMPTS = 5;
const BLOCK_DURATION_MS = 5 * 60 * 1000; // 5 minutos
const STORAGE_KEY = 'login_attempts';

// Schema de validação
const loginSchema = yup.object({
  usuario: yup
    .string()
    .required('Nome de usuário é obrigatório')
    .min(3, 'Nome de usuário deve ter pelo menos 3 caracteres')
    .max(50, 'Nome de usuário deve ter no máximo 50 caracteres')
    .matches(/^[a-zA-Z0-9_.-]+$/, 'Nome de usuário deve conter apenas letras, números, underscore, ponto e hífen'),
  password: yup
    .string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .required('Senha é obrigatória'),
});

type ForgotPasswordStep = 'identify' | 'otp' | 'new_password' | 'final_otp';

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockUntil, setBlockUntil] = useState<number | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);

  // Estados do fluxo Esqueceu a Senha
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState<ForgotPasswordStep>('identify');
  const [forgotUsuario, setForgotUsuario] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotFinalOtp, setForgotFinalOtp] = useState('');
  const [forgotTelefoneMascarado, setForgotTelefoneMascarado] = useState('');
  const [forgotIsAdminDestinatario, setForgotIsAdminDestinatario] = useState(false);
  const [forgotOtpTimer, setForgotOtpTimer] = useState(0);
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotSuccess, setForgotSuccess] = useState<string | null>(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useMuiTheme();
  const { themeId, isDarkMode } = useAppTheme();
  const isCopa = isCopaTheme(themeId);
  const [searchParams, setSearchParams] = useSearchParams();
  const sessaoExpirada = searchParams.get('sessao') === 'expirada';

  // Timer regressivo para reenvio de OTP no Esqueci a Senha
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (forgotOtpTimer > 0) {
      interval = setInterval(() => {
        setForgotOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [forgotOtpTimer]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      usuario: '',
      password: '',
    },
  });

  // Verificar bloqueio ao montar o componente
  useEffect(() => {
    checkBlockStatus();
  }, []);

  // Detectar Caps Lock
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.getModifierState) {
        setCapsLockOn(event.getModifierState('CapsLock'));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('keyup', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('keyup', handleKeyPress);
    };
  }, []);

  // Timer para atualizar tempo restante de bloqueio
  useEffect(() => {
    if (!isBlocked || !blockUntil) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, blockUntil - now);

      if (remaining === 0) {
        clearBlockStatus();
      } else {
        setRemainingTime(Math.ceil(remaining / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isBlocked, blockUntil]);

  // Verificar status de bloqueio
  const checkBlockStatus = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const now = Date.now();

        if (data.blockUntil && data.blockUntil > now) {
          setIsBlocked(true);
          setBlockUntil(data.blockUntil);
          setFailedAttempts(data.attempts || 0);
          setRemainingTime(Math.ceil((data.blockUntil - now) / 1000));
        } else if (data.blockUntil && data.blockUntil <= now) {
          // Bloqueio expirou
          clearBlockStatus();
        } else {
          setFailedAttempts(data.attempts || 0);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar bloqueio:', error);
    }
  };

  // Limpar status de bloqueio
  const clearBlockStatus = () => {
    setIsBlocked(false);
    setBlockUntil(null);
    setFailedAttempts(0);
    setRemainingTime(0);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Processar erro de login de forma inteligente
  const handleLoginError = (err: any) => {
    let errorMessage = 'Erro ao fazer login';

    // Erro de rede
    if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
      errorMessage = '🔌 Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
    }
    // Timeout
    else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      errorMessage = '⏱️ A conexão demorou muito para responder. Tente novamente.';
    }
    // Erro 401 - Credenciais inválidas
    else if (err.response?.status === 401) {
      errorMessage = '🔐 Usuário ou senha incorretos. Verifique suas credenciais e tente novamente.';
    }
    // Erro 403 - Usuário bloqueado/inativo
    else if (err.response?.status === 403) {
      errorMessage = '🚫 Acesso negado. Sua conta pode estar inativa ou bloqueada. Entre em contato com o administrador.';
    }
    // Erro 429 - Too many requests
    else if (err.response?.status === 429) {
      errorMessage = '⚠️ Muitas tentativas de login. Aguarde alguns minutos antes de tentar novamente.';
    }
    // Erro 500 - Erro interno do servidor
    else if (err.response?.status === 500) {
      errorMessage = '⚙️ Erro interno do servidor. Tente novamente em alguns instantes.';
    }
    // Mensagem personalizada do backend
    else if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    }

    console.error('Erro de login:', {
      status: err.response?.status,
      code: err.code,
      message: err.message,
      data: err.response?.data,
    });

    return errorMessage;
  };

  // Incrementar tentativas falhas
  const incrementFailedAttempts = () => {
    const newAttempts = failedAttempts + 1;
    setFailedAttempts(newAttempts);

    if (newAttempts >= MAX_ATTEMPTS) {
      const blockUntilTime = Date.now() + BLOCK_DURATION_MS;
      setIsBlocked(true);
      setBlockUntil(blockUntilTime);
      setRemainingTime(Math.ceil(BLOCK_DURATION_MS / 1000));

      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        attempts: newAttempts,
        blockUntil: blockUntilTime,
      }));
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        attempts: newAttempts,
      }));
    }
  };

  const onSubmit = async (data: LoginCredentials) => {
    // Verificar se está bloqueado
    if (isBlocked) {
      setError(`🔒 Você está temporariamente bloqueado. Aguarde ${formatTime(remainingTime)} para tentar novamente.`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await login(data);

      clearBlockStatus();
      if (sessaoExpirada) {
        setSearchParams({});
      }
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = handleLoginError(err);
      setError(errorMessage);

      // Incrementar tentativas apenas para erros de autenticação
      if (err.response?.status === 401) {
        incrementFailedAttempts();
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Formatar tempo restante
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handler para solicitar OTP (Passo 1: Usuário + Email)
  const handleRequestForgotOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotUsuario.trim() || !forgotEmail.trim()) {
      setForgotError('Preencha seu nome de usuário e e-mail.');
      return;
    }

    setForgotLoading(true);
    setForgotError(null);
    setForgotSuccess(null);

    try {
      const response = await authService.enviarOtpEsqueciSenha(forgotUsuario.trim(), forgotEmail.trim());
      if (response.success) {
        setForgotTelefoneMascarado(response.data?.telefone_mascarado || '');
        setForgotOtpTimer(60);
        setForgotOtp('');
        setForgotStep('otp');
      } else {
        setForgotError(response.message || 'Erro ao enviar código de segurança');
      }
    } catch (err: any) {
      setForgotError(err.response?.data?.message || err.message || 'Erro ao conectar ao servidor');
    } finally {
      setForgotLoading(false);
    }
  };

  // Reenviar primeiro OTP
  const handleResendForgotOtp = async () => {
    if (forgotOtpTimer > 0 || forgotLoading) return;
    setForgotLoading(true);
    setForgotError(null);
    try {
      const response = await authService.enviarOtpEsqueciSenha(forgotUsuario.trim(), forgotEmail.trim());
      if (response.success) {
        setForgotOtpTimer(60);
        setForgotTelefoneMascarado(response.data?.telefone_mascarado || forgotTelefoneMascarado);
      } else {
        setForgotError(response.message || 'Erro ao reenviar código');
      }
    } catch (err: any) {
      setForgotError(err.response?.data?.message || err.message || 'Erro ao reenviar código');
    } finally {
      setForgotLoading(false);
    }
  };

  // Handler para validar primeiro OTP (Passo 2)
  const handleVerifyForgotOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotOtp.trim() || forgotOtp.trim().length !== 6) {
      setForgotError('Informe o código de 6 dígitos recebido.');
      return;
    }

    setForgotLoading(true);
    setForgotError(null);

    try {
      const response = await authService.validarOtpEsqueciSenha(forgotUsuario.trim(), forgotEmail.trim(), forgotOtp.trim());
      if (response.success) {
        setForgotStep('new_password');
      } else {
        setForgotError(response.message || 'Código inválido ou expirado');
      }
    } catch (err: any) {
      setForgotError(err.response?.data?.message || err.message || 'Erro ao validar código');
    } finally {
      setForgotLoading(false);
    }
  };

  // Handler para submeter a nova senha e solicitar o novo código OTP de confirmação (Passo 3)
  const handleResetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotNewPassword || !forgotConfirmPassword) {
      setForgotError('Preencha a nova senha e a confirmação.');
      return;
    }

    if (forgotNewPassword.length < 6) {
      setForgotError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError('As senhas não coincidem. Digite a mesma senha em ambos os campos.');
      return;
    }

    setForgotLoading(true);
    setForgotError(null);

    try {
      const response = await authService.enviarOtpNovaSenha(forgotUsuario.trim(), forgotEmail.trim(), forgotNewPassword);
      if (response.success) {
        if (response.data?.telefone_mascarado) {
          setForgotTelefoneMascarado(response.data.telefone_mascarado);
        }
        setForgotIsAdminDestinatario(!!response.data?.is_admin_destinatario);
        setForgotOtpTimer(60);
        setForgotFinalOtp('');
        setForgotStep('final_otp');
      } else {
        setForgotError(response.message || 'Erro ao solicitar confirmação de nova senha');
      }
    } catch (err: any) {
      setForgotError(err.response?.data?.message || err.message || 'Erro ao enviar código de confirmação');
    } finally {
      setForgotLoading(false);
    }
  };

  // Reenviar segundo OTP (confirmação da nova senha)
  const handleResendFinalOtp = async () => {
    if (forgotOtpTimer > 0 || forgotLoading) return;
    setForgotLoading(true);
    setForgotError(null);
    try {
      const response = await authService.enviarOtpNovaSenha(forgotUsuario.trim(), forgotEmail.trim(), forgotNewPassword);
      if (response.success) {
        setForgotOtpTimer(60);
        if (response.data?.telefone_mascarado) {
          setForgotTelefoneMascarado(response.data.telefone_mascarado);
        }
        setForgotIsAdminDestinatario(!!response.data?.is_admin_destinatario);
      } else {
        setForgotError(response.message || 'Erro ao reenviar código');
      }
    } catch (err: any) {
      setForgotError(err.response?.data?.message || err.message || 'Erro ao reenviar código');
    } finally {
      setForgotLoading(false);
    }
  };

  // Handler para validar o segundo OTP e concluir a redefinição da nova senha (Passo 4)
  const handleConfirmFinalOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotFinalOtp.trim() || forgotFinalOtp.trim().length !== 6) {
      setForgotError('Informe o código de confirmação de 6 dígitos recebido.');
      return;
    }

    setForgotLoading(true);
    setForgotError(null);

    try {
      const response = await authService.resetPasswordWithOtp(
        forgotUsuario.trim(),
        forgotEmail.trim(),
        forgotNewPassword,
        forgotFinalOtp.trim()
      );
      if (response.success) {
        alert('Senha redefinida com sucesso! Você já pode fazer login com sua nova senha.');
        resetForgotState();
      } else {
        setForgotError(response.message || 'Erro ao validar código de confirmação');
      }
    } catch (err: any) {
      setForgotError(err.response?.data?.message || err.message || 'Erro ao redefinir senha');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotState = () => {
    setIsForgotPassword(false);
    setForgotStep('identify');
    setForgotUsuario('');
    setForgotEmail('');
    setForgotOtp('');
    setForgotFinalOtp('');
    setForgotIsAdminDestinatario(false);
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotError(null);
    setForgotSuccess(null);
  };

  return (
    <>
      <Helmet>
        <title>{isForgotPassword ? 'Redefinir Senha - SysUp' : 'Login - SysUp'}</title>
        <meta name="description" content="Faça login no sistema SysUp" />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          background: getLoginPageBackground(theme, themeId),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 10,
            color: '#ffffff',
          }}
        >
          <ThemeMenuButton isDarkMode={isDarkMode} isCopa={isCopa} size="medium" />
        </Box>

        <Container maxWidth="sm">
          <Card elevation={0} sx={getLoginCardSx(theme, themeId)}>
            <Box
              sx={{
                background: getLoginCardHeaderBackground(theme, themeId),
                color: '#ffffff',
                p: 2.5,
                textAlign: 'center',
                borderBottom:
                  themeId === 'copa'
                    ? '1px solid rgba(255, 204, 0, 0.25)'
                    : themeId === 'dark'
                      ? '1px solid rgba(59, 130, 246, 0.2)'
                      : 'none',
              }}
            >
              <Box
                component="img"
                src={logoWhite}
                alt="SysUP"
                sx={{
                  height: { xs: 44, sm: 52 },
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  mx: 'auto',
                }}
              />
              <Typography variant="subtitle2" sx={{ mt: 1, opacity: 0.92 }}>
                Sistema de Gerenciamento ISP
              </Typography>
            </Box>

            <CardContent sx={{ p: 2.5 }}>
              {isForgotPassword ? (
                /* ─── FLUXO ESQUECEU SUA SENHA ─── */
                <Box>
                  <Typography variant="h6" component="h2" textAlign="center" mb={1} fontWeight={700}>
                    {forgotStep === 'identify' && 'Recuperar Acesso'}
                    {forgotStep === 'otp' && 'Código de Segurança'}
                    {forgotStep === 'new_password' && 'Criar Nova Senha'}
                  </Typography>

                  {forgotError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {forgotError}
                    </Alert>
                  )}

                  {forgotSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {forgotSuccess}
                    </Alert>
                  )}

                  {/* ETAPA 1: Usuário e E-mail */}
                  {forgotStep === 'identify' && (
                    <Box component="form" onSubmit={handleRequestForgotOtp}>
                      <Typography variant="body2" color="text.secondary" textAlign="center" mb={2.5}>
                        Informe seu <strong>nome de usuário</strong> e <strong>e-mail cadastrado</strong> para receber o código via WhatsApp.
                      </Typography>

                      <TextField
                        fullWidth
                        label="Nome de Usuário"
                        placeholder="Digite seu usuário de acesso"
                        value={forgotUsuario}
                        onChange={(e) => {
                          setForgotUsuario(e.target.value);
                          if (forgotError) setForgotError(null);
                        }}
                        margin="dense"
                        disabled={forgotLoading}
                        autoFocus
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        fullWidth
                        label="E-mail Cadastrado"
                        type="email"
                        placeholder="seuemail@provedor.com.br"
                        value={forgotEmail}
                        onChange={(e) => {
                          setForgotEmail(e.target.value);
                          if (forgotError) setForgotError(null);
                        }}
                        margin="dense"
                        disabled={forgotLoading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={forgotLoading || !forgotUsuario.trim() || !forgotEmail.trim()}
                        sx={{ mt: 2.5, mb: 1, py: 1 }}
                      >
                        {forgotLoading ? 'Enviando código...' : 'Confirmar e Enviar Código'}
                      </Button>

                      <Button
                        fullWidth
                        variant="text"
                        onClick={resetForgotState}
                        disabled={forgotLoading}
                        startIcon={<ArrowBack />}
                        sx={{ textTransform: 'none', color: 'text.secondary', mt: 0.5 }}
                      >
                        Voltar para o Login
                      </Button>
                    </Box>
                  )}

                  {/* ETAPA 2: Código OTP via WhatsApp */}
                  {forgotStep === 'otp' && (
                    <Box component="form" onSubmit={handleVerifyForgotOtp} textAlign="center">
                      <Typography variant="body2" color="text.secondary" mb={1.5}>
                        Enviamos um código de segurança via <strong>WhatsApp</strong> para seu telefone:
                      </Typography>

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          bgcolor: 'action.hover',
                          px: 2,
                          py: 0.75,
                          borderRadius: 2,
                          mb: 2,
                        }}
                      >
                        <WhatsApp sx={{ color: '#16a34a', fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={700}>
                          {forgotTelefoneMascarado}
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        label="Código de 6 dígitos"
                        placeholder="000000"
                        value={forgotOtp}
                        onChange={(e) => {
                          setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                          if (forgotError) setForgotError(null);
                        }}
                        margin="dense"
                        disabled={forgotLoading}
                        autoFocus
                        inputProps={{
                          maxLength: 6,
                          style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.4rem', fontWeight: 'bold' }
                        }}
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 1, mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Não recebeu o código?
                        </Typography>
                        <Button
                          size="small"
                          onClick={handleResendForgotOtp}
                          disabled={forgotOtpTimer > 0 || forgotLoading}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', p: 0 }}
                        >
                          {forgotOtpTimer > 0 ? `Reenviar em ${forgotOtpTimer}s` : 'Reenviar código'}
                        </Button>
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={forgotLoading || forgotOtp.trim().length !== 6}
                        endIcon={<CheckCircle />}
                        sx={{ mb: 1, py: 1 }}
                      >
                        {forgotLoading ? 'Validando...' : 'Confirmar Código'}
                      </Button>

                      <Button
                        fullWidth
                        variant="text"
                        onClick={() => {
                          setForgotError(null);
                          setForgotStep('identify');
                        }}
                        disabled={forgotLoading}
                        startIcon={<ArrowBack />}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                      >
                        Voltar
                      </Button>
                    </Box>
                  )}

                  {/* ETAPA 3: Nova Senha e Confirmação */}
                  {forgotStep === 'new_password' && (
                    <Box component="form" onSubmit={handleResetNewPassword}>
                      <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                        Código validado! Crie sua nova senha de acesso:
                      </Typography>

                      <TextField
                        fullWidth
                        label="Nova Senha"
                        type={showForgotNewPassword ? 'text' : 'password'}
                        placeholder="Mínimo de 6 caracteres"
                        value={forgotNewPassword}
                        onChange={(e) => {
                          setForgotNewPassword(e.target.value);
                          if (forgotError) setForgotError(null);
                        }}
                        margin="dense"
                        disabled={forgotLoading}
                        autoFocus
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowForgotNewPassword(!showForgotNewPassword)}
                                edge="end"
                                disabled={forgotLoading}
                              >
                                {showForgotNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Confirmar Nova Senha"
                        type={showForgotConfirmPassword ? 'text' : 'password'}
                        placeholder="Digite a nova senha novamente"
                        value={forgotConfirmPassword}
                        onChange={(e) => {
                          setForgotConfirmPassword(e.target.value);
                          if (forgotError) setForgotError(null);
                        }}
                        margin="dense"
                        disabled={forgotLoading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowForgotConfirmPassword(!showForgotConfirmPassword)}
                                edge="end"
                                disabled={forgotLoading}
                              >
                                {showForgotConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={forgotLoading || !forgotNewPassword || !forgotConfirmPassword}
                        sx={{ mt: 2.5, mb: 1, py: 1 }}
                      >
                        {forgotLoading ? 'Enviando código...' : 'Continuar e Confirmar'}
                      </Button>

                      <Button
                        fullWidth
                        variant="text"
                        onClick={resetForgotState}
                        disabled={forgotLoading}
                        sx={{ textTransform: 'none', color: 'text.secondary', mt: 0.5 }}
                      >
                        Cancelar e Voltar ao Login
                      </Button>
                    </Box>
                  )}

                  {/* ETAPA 4: Confirmação final com novo código OTP */}
                  {forgotStep === 'final_otp' && (
                    <Box component="form" onSubmit={handleConfirmFinalOtp} textAlign="center">
                      <Typography variant="body2" color="text.secondary" mb={1.5}>
                        {forgotIsAdminDestinatario ? (
                          <>
                            Para sua segurança, o código de autorização foi enviado para o WhatsApp de um <strong>Administrador do sistema</strong>:
                          </>
                        ) : (
                          <>
                            Para sua segurança, enviamos um <strong>novo código de confirmação</strong> para o WhatsApp:
                          </>
                        )}
                      </Typography>

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          bgcolor: 'action.hover',
                          px: 2,
                          py: 0.75,
                          borderRadius: 2,
                          mb: 2,
                        }}
                      >
                        <WhatsApp sx={{ color: '#16a34a', fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={700}>
                          {forgotTelefoneMascarado}
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        label="Novo Código de 6 dígitos"
                        placeholder="000000"
                        value={forgotFinalOtp}
                        onChange={(e) => {
                          setForgotFinalOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                          if (forgotError) setForgotError(null);
                        }}
                        margin="dense"
                        disabled={forgotLoading}
                        autoFocus
                        inputProps={{
                          maxLength: 6,
                          style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.4rem', fontWeight: 'bold' }
                        }}
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 1, mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Não recebeu o novo código?
                        </Typography>
                        <Button
                          size="small"
                          onClick={handleResendFinalOtp}
                          disabled={forgotOtpTimer > 0 || forgotLoading}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', p: 0 }}
                        >
                          {forgotOtpTimer > 0 ? `Reenviar em ${forgotOtpTimer}s` : 'Reenviar código'}
                        </Button>
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={forgotLoading || forgotFinalOtp.trim().length !== 6}
                        endIcon={<CheckCircle />}
                        sx={{ mb: 1, py: 1 }}
                      >
                        {forgotLoading ? 'Confirmando...' : 'Finalizar Redefinição'}
                      </Button>

                      <Button
                        fullWidth
                        variant="text"
                        onClick={() => {
                          setForgotError(null);
                          setForgotStep('new_password');
                        }}
                        disabled={forgotLoading}
                        startIcon={<ArrowBack />}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                      >
                        Voltar para Alterar Senha
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                /* ─── TELA NORMAL DE LOGIN ─── */
                <>
                  <Typography variant="h6" component="h2" textAlign="center" mb={2}>
                    Bem-vindo de volta!
                  </Typography>

                  {/* Alerta de sessão expirada */}
                  {sessaoExpirada && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Sua sessão expirou. Faça login novamente para continuar.
                    </Alert>
                  )}

                  {/* Alerta de bloqueio */}
                  {isBlocked && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      🔒 Muitas tentativas falhas de login. Você está bloqueado por {formatTime(remainingTime)}.
                    </Alert>
                  )}

                  {/* Alerta de tentativas */}
                  {!isBlocked && failedAttempts > 0 && failedAttempts < MAX_ATTEMPTS && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      ⚠️ Tentativa {failedAttempts} de {MAX_ATTEMPTS}. Após {MAX_ATTEMPTS} tentativas falhas, você será bloqueado por 5 minutos.
                    </Alert>
                  )}

                  {/* Alerta de Caps Lock */}
                  {capsLockOn && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      ⚠️ Caps Lock está ativado
                    </Alert>
                  )}

                  {/* Alerta de erro */}
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                      name="usuario"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Nome de Usuário"
                          type="text"
                          placeholder="Digite seu nome de usuário"
                          error={!!errors.usuario}
                          helperText={errors.usuario?.message}
                          margin="dense"
                          disabled={isBlocked}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Senha"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Digite sua senha"
                          error={!!errors.password}
                          helperText={errors.password?.message}
                          autoComplete="current-password"
                          disabled={isBlocked}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={togglePasswordVisibility}
                                  edge="end"
                                  disabled={isBlocked}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading || isBlocked}
                      startIcon={<Login />}
                      sx={{
                        mt: 2,
                        mb: 1,
                        py: 1,
                        fontSize: '1rem',
                      }}
                    >
                      {loading ? 'Entrando...' : isBlocked ? `Bloqueado (${formatTime(remainingTime)})` : 'Entrar'}
                    </Button>
                  </form>

                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      ou
                    </Typography>
                  </Divider>

                  <Box textAlign="center" mt={1}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      color="primary"
                      onClick={() => {
                        setIsForgotPassword(true);
                        setForgotStep('identify');
                        setForgotError(null);
                        setForgotSuccess(null);
                      }}
                      sx={{ fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Esqueceu sua senha?
                    </Link>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          <Box textAlign="center" mt={2}>
            <Typography
              variant="body2"
              sx={{
                color: getLoginFooterTextColor(themeId),
                opacity: 0.85,
              }}
            >
              © 2025 SysUp. Todos os direitos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LoginPage;